import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Download, Search } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";

interface ReportFilterProps {
  onSearch: (filters: any) => void;
  onExportCsv?: (filters: any) => void;
  onExportPdf?: (filters: any) => void;
}

export const ReportFilters: React.FC<ReportFilterProps> = ({ 
  onSearch, 
  onExportCsv,
  onExportPdf
}) => {
  const [date, setDate] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [candidateName, setCandidateName] = useState("");

  const getCurrentFilters = () => ({
    startDate: date.from ? format(date.from, "yyyy-MM-dd") : undefined,
    endDate: date.to ? format(date.to, "yyyy-MM-dd") : undefined,
    candidateName: candidateName || undefined,
  });

  const handleSearch = () => {
    onSearch(getCurrentFilters());
  };

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="grid gap-2">
            <label className="text-sm font-medium">Date Range</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="date"
                  variant={"outline"}
                  className={cn(
                    "w-[300px] justify-start text-left font-normal",
                    !date.from && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date?.from ? (
                    date.to ? (
                      <>
                        {format(date.from, "LLL dd, y")} -{" "}
                        {format(date.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(date.from, "LLL dd, y")
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={date?.from}
                  selected={date}
                  onSelect={(range: any) => setDate(range)}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid gap-2 flex-1 min-w-[200px]">
            <label className="text-sm font-medium">Candidate Name</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search candidates..."
                className="pl-8"
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleSearch} className="gap-2">
            <Search className="h-4 w-4" />
            Apply Filters
          </Button>

          <div className="flex gap-2 ml-auto">
            {onExportCsv && (
              <Button 
                variant="outline" 
                className="gap-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                onClick={() => onExportCsv(getCurrentFilters())}
              >
                <Download className="h-4 w-4" />
                CSV
              </Button>
            )}
            {onExportPdf && (
              <Button 
                variant="outline" 
                className="gap-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                onClick={() => onExportPdf(getCurrentFilters())}
              >
                <Download className="h-4 w-4" />
                PDF
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
